import { useState } from 'react';
import { ArrowUpRight, Check, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { OverviewSectionCard } from '@/components/agent/overview/OverviewSectionCard';
import { ActivationPackWithTarget } from '@/types/activation/activation-pack';
import { ActivationFunnelService } from '@/services/analytics/ActivationFunnelService';
import { ActivationPackService } from '@/services/database/ActivationPackService';
import { SendLinkToTeam } from '../shared/SendLinkToTeam';

interface PackPresentationSectionProps {
  pack: ActivationPackWithTarget;
  brand?: string;
}

export const PackPresentationSection = ({ pack, brand = '#4E9CFF' }: PackPresentationSectionProps) => {
  const [copied, setCopied] = useState(false);

  const name = ActivationPackService.getTargetName(pack);
  const presentationUrl = ActivationPackService.getPresentationUrl(pack);
  const secondary = pack.agent?.branding?.secondaryColor || '#FF4DB8';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(presentationUrl);
      ActivationFunnelService.packShared(
        ActivationFunnelService.packGenieId(pack),
        'link',
        { link: 'presentation' },
      );
      setCopied(true);
      toast.success('Presentation link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  return (
    <OverviewSectionCard
      title="Activation presentation"
      subtitle="An immersive, branded walkthrough built for your Genie — send it to your team and they get set up in minutes."
      genieIcon="play"
      brand={brand}
    >
      <div className="space-y-5">
        {/* Branded preview */}
        <a
          href={presentationUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block rounded-2xl p-[1.5px] bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] group"
        >
          <div
            className="rounded-[14px] overflow-hidden px-6 py-10 text-center relative"
            style={{ background: `linear-gradient(135deg, ${brand}, ${secondary})` }}
          >
            <div className="text-[11px] font-semibold tracking-[0.22em] text-white/80 uppercase">
              Immersive presentation
            </div>
            <div className="text-2xl md:text-3xl font-bold text-white mt-1.5">{name}</div>
            <div className="text-sm text-white/85 mt-2 max-w-md mx-auto">
              A guided walkthrough your team reviews in minutes — ending with one-tap setup.
            </div>
            {/* page dots — hints at the multi-page flow */}
            <div className="flex items-center justify-center gap-1.5 mt-5">
              {Array.from({ length: 7 }).map((_, i) => (
                <span
                  key={i}
                  className="rounded-full"
                  style={{ width: 7, height: 7, background: i === 0 ? '#fff' : 'rgba(255,255,255,0.4)' }}
                />
              ))}
            </div>
            <div className="inline-flex items-center gap-1.5 mt-6 px-4 py-2 rounded-xl bg-white font-semibold text-sm group-hover:scale-105 transition-transform" style={{ color: brand }}>
              Open presentation
              <ArrowUpRight className="w-4 h-4" />
            </div>
          </div>
        </a>

        {/* Send / copy */}
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
            Presentation link
          </label>
          <div className="flex items-center gap-2">
            <code className="text-xs bg-gray-100 px-3 py-2 rounded-lg border flex-1 truncate text-gray-700">
              {presentationUrl}
            </code>
            <Button variant="outline" size="sm" onClick={handleCopy} className="shrink-0">
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-1.5 text-green-600" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-1.5" />
                  Copy link
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-1.5">
            They skim it, then download the app, scan the QR, or save the link — all from the last page.
          </p>
        </div>

        <SendLinkToTeam
          packId={pack.id}
          kind="presentation"
          genieId={ActivationFunnelService.packGenieId(pack)}
          brand={brand}
        />
      </div>
    </OverviewSectionCard>
  );
};
